import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { debounce } from '@mui/material'
import { catalogItemType, catalogModType } from './catalogTypes'
import { gsaItemType } from './CatalogTypes/gsaTypes'
import { itesArHeaderType, itesArLineType, sewpTrHeaderType, sewpTrLineType } from 'src/types'

export type CatalogTableItem =
  | (catalogItemType & { kind: 'catalog' })
  | (catalogModType & { kind: 'modification' })
  | (gsaItemType & { kind: 'gsa' })
  | (sewpTrLineType & { id: string; kind: 'sewpTr' })
  | (itesArLineType & { id: string; kind: 'itesAr' })
  | (sewpTrHeaderType & { id: string; kind: 'sewpTrHeader' })
  | (itesArHeaderType & { id: string; kind: 'itesArHeader' })

export type catalogTableItemListType = CatalogTableItem[]

type RawCatalogRow =
  | catalogItemType
  | catalogModType
  | gsaItemType
  | sewpTrLineType
  | sewpTrHeaderType
  | itesArLineType
  | itesArHeaderType

export function useCatalogData(
  contractNumber: string,
  page: number,
  pageSize: number,
  filters: { [k: string]: string },
  options?: { modType?: string },
) {
  const [catalogItems, setCatalogItems] = useState<catalogTableItemListType>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState<'' | 'loading' | 'error'>('')
  const modType = options?.modType
  const requestIdRef = useRef(0)

  async function load() {
    if (!contractNumber) return
    const requestId = ++requestIdRef.current
    setLoading('loading')
    try {
      const activeFilters = {
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
        ...(modType ? { modType } : {}),
      }
      const res = await axios.get<{ total: number; list: RawCatalogRow[] }>(`/catalog/${contractNumber}`, {
        params: {
          page,
          pageSize,
          filters: JSON.stringify(activeFilters),
        },
      })
      if (requestIdRef.current !== requestId) return

      const raw = res.data.list
      const list: catalogTableItemListType =
        raw.length === 0
          ? []
          : 'CatalogClin' in raw[0]
          ? (raw as itesArLineType[]).map(item => ({ ...item, id: item.RequestLineId, kind: 'itesAr' as const }))
          : 'ArNumber' in raw[0]
          ? (raw as itesArHeaderType[]).map(item => ({ ...item, id: item.RequestId, kind: 'itesArHeader' as const }))
          : 'RequestLineId' in raw[0]
          ? (raw as sewpTrLineType[]).map(item => ({ ...item, id: item.RequestLineId, kind: 'sewpTr' as const }))
          : 'RequestId' in raw[0]
          ? (raw as sewpTrHeaderType[]).map(item => ({ ...item, id: item.RequestId, kind: 'sewpTrHeader' as const }))
          : 'sourceImpresQuoteNumber' in raw[0]
          ? (raw as gsaItemType[]).map(item => ({ ...item, kind: 'gsa' as const }))
          : modType
          ? (raw as catalogModType[]).map(item => ({ ...item, kind: 'modification' as const }))
          : (raw as catalogItemType[]).map(item => ({ ...item, kind: 'catalog' as const }))
      setCatalogItems(list)
      setTotal(res.data.total)
      setLoading('')
    } catch (err) {
      if (requestIdRef.current !== requestId) return
      console.error('Failed to load catalog', err)
      setLoading('error')
    }
  }

  const loadRef = useRef(load)
  loadRef.current = load

  const debouncedLoad = useRef(debounce(() => loadRef.current(), 500)).current

  useEffect(() => {
    load()
  }, [contractNumber, page, pageSize, modType])

  useEffect(() => {
    if (!contractNumber) return
    debouncedLoad()
  }, [filters])

  async function updateProperty(ids: string[], property: string, value: string) {
    return new Promise<void>((resolve, reject) => {
      axios
        .put(`/catalog/${contractNumber}`, { ids, trLine: { [property]: value } })
        .then(() => {
          setCatalogItems(prev => prev.map(item => (ids.includes(item.id) ? { ...item, [property]: value } : item)))
          resolve()
        })
        .catch(err => {
          reject(err)
          console.error('Failed to update catalog property', err)
        })
    })
  }

  return { catalogItems, total, loading, reload: load, updateProperty }
}
